/**
 * sidebarModuleSection.ts — WHICH module the main sidebar shows when the route has none.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ AN APP IS NOT A MODULE, AND AN EMPTY MODULE SECTION IS NOT "NO ACTIVE     ║
 * ║     PAGE". THAT CONFLATION IS THE BUG THIS FILE EXISTS TO END.                ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * Webmail, My tasks, Chat and Directory are APPS — open to every employee, never gated by
 * module settings (Lis, 2026-09-09, restated 2026-10-05). They are not modules, so no dock
 * tile owns them, so `dockKeyForPath('/mail')` is `null`, so `contextSectionsForPath` is
 * `[]`, so the sidebar below the Apps rail rendered **nothing at all**.
 *
 * Lis's earlier brief said the module section must show **no active page** and **no
 * "Active module" card**. It was built as an EMPTY SECTION, and on 2026-10-06 she said
 * plainly that is not what she meant:
 *
 *   > *"My earlier brief said 'no active page'; I did NOT mean an empty section. Today it
 *   > renders empty. Fix that."*
 *
 * The design screens agree and always did — *"Inbox — main sidebar expanded · 1440"* and
 * *"Sidebar — Webmail clicked"* both draw the group header **CRM & Sales**, the **CRM**
 * row with its chevron, and its eight pages, with none of them highlighted.
 *
 * ── THE RULE, IN LIS'S ORDER ────────────────────────────────────────────────────
 *   1. The route owns a module  ->  that module. (Unchanged behaviour, every other page.)
 *   2. Otherwise, the module you were LAST in, if you can still access it.
 *   3. Otherwise, the FIRST module you can access, in the dock's own order.
 *   4. Only modules you can access ever appear, at any step.
 *
 * ⚠️ **STEP 2 CANNOT CHECK ITSELF.** `readLastModule` validates that the stored value is a
 * current dock key; it knows nothing about who is signed in. Access is re-checked HERE, on
 * every read, because a person's module access changes under a value that does not.
 *
 * ── ⚠️ WHY THIS RETURNS `fromRoute` AND NOT JUST A KEY ──────────────────────────
 * The caller needs the two cases to look different, because one of them must NOT light up
 * the "Active module" card. A key alone cannot say *"these sections are a fallback"*, and
 * a caller that re-derived it would be a second definition of "is this route in a module",
 * free to disagree with `dockKeyForPath`. So the answer carries its own provenance.
 */

import { DOCK, dockKeyForPath, type DockItem, type DockKey } from '../components/layout/dock';
import type { ModuleKey } from '../types';

export interface ModuleSectionChoice {
  /** The dock item whose sections to render, or `null` when nothing is accessible. */
  key: DockKey | null;
  /**
   * `true` when the route itself owns this module — the normal case on every page except
   * an App. **`false` means the section is a fallback**, and the caller must not render
   * the "Active module" card for it.
   */
  fromRoute: boolean;
}

/**
 * Can this person open this dock tile?
 *
 * A tile with no `modules` (Dashboard, Admin) is open to everyone — existing app behaviour,
 * and deliberately so: `/admin/*` is mostly cross-cutting rather than module-gated, and
 * associating a module with it once hid the whole tile (PR #67, Lis lost it).
 */
export function dockItemAccessible(
  item: DockItem,
  canAccessModule: (key: ModuleKey) => boolean,
): boolean {
  if (!item.modules || item.modules.length === 0) return true;
  return item.modules.some(canAccessModule);
}

export function chooseModuleSection(
  pathname: string,
  opts: {
    /** `readLastModule()`'s answer, or `null`. Already validated as a dock key. */
    remembered: DockKey | null;
    canAccessModule: (key: ModuleKey) => boolean;
  },
): ModuleSectionChoice {
  const { remembered, canAccessModule } = opts;

  /* 1. The route owns a module. */
  const fromRoute = dockKeyForPath(pathname);
  if (fromRoute) return { key: fromRoute, fromRoute: true };

  const accessible = (key: DockKey): boolean => {
    const item = DOCK.find(d => d.key === key);
    return !!item && dockItemAccessible(item, canAccessModule);
  };

  /* 2. The module you were last in — only if you can still get into it. */
  if (remembered && accessible(remembered)) {
    return { key: remembered, fromRoute: false };
  }

  /* 3. The first module you can access, in the dock's own order. */
  return { key: firstAccessibleDockKey(canAccessModule), fromRoute: false };
}

/**
 * The first tile in `dock` order that this person can open, or `null`.
 *
 * ⚠️ **`dock` IS INJECTABLE FOR ONE REASON, AND IT IS NOT FLEXIBILITY.** The real `DOCK`
 * begins with Dashboard, which carries no `modules` and is therefore accessible to
 * everyone — so against the real list *"the first accessible tile"* and *"the first tile"*
 * give the same answer for every possible input, and a mutation replacing one with the
 * other CANNOT be detected. It was written that way first and the mutation survived.
 * The parameter exists so a test can hand in a list whose first entry is gated and denied,
 * which is the only fixture where the right and wrong answers differ.
 */
export function firstAccessibleDockKey(
  canAccessModule: (key: ModuleKey) => boolean,
  dock: readonly DockItem[] = DOCK,
): DockKey | null {
  const first = dock.find(d => dockItemAccessible(d, canAccessModule));
  return first ? first.key : null;
}

/**
 * Add the fallback module's sections to the set of expanded sections.
 *
 * ⛔ A FALLBACK SECTION HAS TO OPEN ITSELF, BECAUSE NOTHING ELSE CAN OPEN IT. The
 * sidebar's existing rule expands a section when one of its pages is the current route.
 * On an App route none of them is, by definition — so the module would draw as a single
 * collapsed row with a chevron and no pages. Both design screens show it open with its
 * pages listed and none highlighted.
 *
 * ⚠️ It only ADDS, and it returns the SAME array when there is nothing to add — the
 * caller feeds this straight into a state setter, and a fresh array every time would
 * re-render for ever.
 */
export function openSectionsWithFallback(
  open: readonly string[],
  choice: ModuleSectionChoice,
): string[] {
  if (choice.fromRoute) return open as string[];
  const missing = sectionLabelsForChoice(choice).filter(l => !open.includes(l));
  return missing.length ? [...open, ...missing] : (open as string[]);
}

/**
 * The NAV section labels to render, given a choice.
 *
 * ⚠️ Deliberately NOT `contextSectionsForPath`. That function answers *"what does this
 * ROUTE own"*, and the whole point here is the case where the route owns nothing. Calling
 * it with a path would mean inventing a path for a module, which is how a fallback starts
 * pretending to be a location.
 */
export function sectionLabelsForChoice(choice: ModuleSectionChoice): string[] {
  if (!choice.key) return [];
  return DOCK.find(d => d.key === choice.key)?.sections ?? [];
}
